import * as d3 from 'd3';
import { groupBy, isNil } from 'lodash-es';
import moment from 'moment-timezone';
import { Resource } from '../Resource';
import { extractAggregations, getValuesCountOverThreshold } from '../core/charts';
import { extractHistoryValues } from '../core/charts/charts';
import { getMetricsToFetch, getPeriodAggregationPerMetric } from '../core/history';
import { getYDomainPerMetric } from '../core/measurements';
import { getConverters, getUnits } from '../core/units';
import { DataSources } from '../enums';
import { isMetricAggregates, isMetricWithHorizon, metricByAggregate } from '../enums/MetricIds';
import cumulativeSumRequester from './cumulativeSum';
import measurements from './measurements';
import { AggregationType } from './measurements.type';
import { isMeasurementReportType } from './measurementsReports.types';
import { plotMutationExternalInvalidationKeys } from './plots';
import thresholdsRequester from './thresholds';
import vpdRequester from './vpd';
export const SERIAL_PREFIX = 'S_';
function extractDomain(deviceDataPoint) {
    return d3.extent(deviceDataPoint.reduce((acc, x) => {
        if (!isNil(x.value)) {
            acc.push(x.value);
        }
        return acc;
    }, []));
}
/** Amount of rain in mm to consider a day as rainy */
const RAIN_THRESHOLD = 0.5;
export const CHART_REQUEST_INVALIDATION_KEY = ['history'];
const charts = Resource((requesterBuilder, config) => {
    const fetchData = async ({ kind, since, before, metrics, isAggregateBySerial, isAggregateByMetric, timeStep, deviceId, plotId, cumulativeMetric, base, gdd_t_max, params, thresholdId, source, stationId, canUseVPDFromDevice }) => {
        const isPlotRequest = kind === 'plot';
        const measurementsRequester = measurements(requesterBuilder, config)[kind === 'plot' ? 'plots' : kind === 'device' ? 'devices' : 'stations'].histories.get1_1({
            before,
            metrics,
            since,
            aggregateByMetric: isAggregateByMetric,
            aggregateHorizon: !isAggregateBySerial,
            span: timeStep,
            deviceId,
            plotId,
            stationId
        });
        const cumulativeSumRequest = !isNil(cumulativeMetric)
            ? cumulativeSumRequester(requesterBuilder, config).history({
                base,
                gdd_t_max,
                before,
                deviceId,
                metric: cumulativeMetric,
                plotId,
                since,
                span: timeStep,
                stationId: 'stationId' in params ? params.stationId : undefined
            })
            : undefined;
        const vpdRequest = isPlotRequest && metrics.length === 1 && metrics.includes('VPD')
            ? vpdRequester(requesterBuilder, config).history({
                plotId: plotId,
                since,
                span: timeStep,
                before,
                source: canUseVPDFromDevice ? source ?? DataSources.measurement : undefined
            })
            : undefined;
        const thresholdResource = thresholdsRequester(requesterBuilder, config);
        const thresholdProfileRequest = isPlotRequest && !isNil(thresholdId)
            ? thresholdResource.getThresholdsProfile(thresholdId)
            : undefined;
        const defaultThresholdRequest = isPlotRequest && isNil(thresholdId)
            ? thresholdResource.getDefaultThresholds({
                metric: metrics.includes('HPOT') ? 'HPOT' : 'U_CAPA'
            })
            : undefined;
        const networkCalls = [
            measurementsRequester.request(),
            cumulativeSumRequest?.request(),
            thresholdProfileRequest?.request(),
            defaultThresholdRequest?.request(),
            vpdRequest?.request()
        ];
        return Promise.all(networkCalls);
    };
    return {
        history: (params) => {
            const { base, before, canUseVPDFromDevice, cumulativeMetric, gdd_t_max, highlightThresholds, isAggregateByMetric, isAggregateBySerial, metrics: metricsAsked, overriddenDomainByMetric, since, source, thresholdId, timeStep } = params;
            const metrics = getMetricsToFetch(metricsAsked);
            const result = {
                invalidationKey: CHART_REQUEST_INVALIDATION_KEY,
                key: [
                    `history_${'plotId' in params ? params.plotId : 'deviceId' in params ? params.deviceId : params.stationId}_${since}_${before}_${timeStep}_${base}_${gdd_t_max}_${metricsAsked.join(',')}_${highlightThresholds}_${isAggregateBySerial}_${isAggregateByMetric}_${cumulativeMetric}_${source}_${thresholdId}`
                ],
                fetchAndTransform: () => {
                    throw new Error('not implemented');
                },
                request: async () => {
                    plotMutationExternalInvalidationKeys.add(CHART_REQUEST_INVALIDATION_KEY[0]);
                    const isPlotRequest = 'plotId' in params;
                    const plotId = isPlotRequest ? params.plotId : undefined;
                    const isDeviceRequest = 'deviceId' in params;
                    const deviceId = isDeviceRequest ? params.deviceId : undefined;
                    const isStationRequest = 'stationId' in params;
                    const stationId = isStationRequest ? params.stationId : undefined;
                    const [measurementResult, cumulativeSumResult, thresholdProfileResult, defaultThresholdResult, vpdResult] = await fetchData({
                        base,
                        gdd_t_max,
                        before,
                        cumulativeMetric,
                        deviceId,
                        isAggregateBySerial,
                        isAggregateByMetric,
                        kind: isPlotRequest ? 'plot' : isDeviceRequest ? 'device' : 'station',
                        metrics,
                        params,
                        plotId,
                        since,
                        thresholdId,
                        timeStep,
                        source,
                        stationId,
                        canUseVPDFromDevice
                    });
                    const cumulativeSum = cumulativeSumResult?.cumulativeSum;
                    const thresholds = isPlotRequest
                        ? thresholdProfileResult?.thresholds ??
                            (params.soilTexture ? defaultThresholdResult?.[params.soilTexture] : undefined)
                        : undefined;
                    const aggregationsByMetric = new Map();
                    const dataByMetric = new Map();
                    const dataByMetricBySerialOrHorizon = new Map();
                    const dataByDateByMetric = new Map();
                    const dataPointByDateByMetricWithHorizonByHorizon = new Map();
                    let data = [];
                    const normalizerByUnit = new Map();
                    const onChartMetrics = !isNil(cumulativeMetric) ? [cumulativeMetric] : metrics;
                    // COLLECTING ALL INFOS
                    onChartMetrics.forEach((metricOrAggregationMetric) => {
                        let aggregationType = AggregationType.avg;
                        if (metricOrAggregationMetric.endsWith('N') ||
                            metricOrAggregationMetric.endsWith('-min')) {
                            aggregationType = AggregationType.min;
                        }
                        else if (metricOrAggregationMetric.endsWith('X') ||
                            metricOrAggregationMetric.endsWith('-max') ||
                            metricOrAggregationMetric === 'FXY') {
                            aggregationType = AggregationType.max;
                        }
                        else if (metricOrAggregationMetric === 'DD') {
                            aggregationType = AggregationType.circular_avg;
                        }
                        const originalMetric = isMetricAggregates(metricOrAggregationMetric)
                            ? metricByAggregate[metricOrAggregationMetric]
                            : metricOrAggregationMetric;
                        let metricData = [];
                        const metricDataByDepthOrSerial = new Map();
                        if (isMetricWithHorizon(metricOrAggregationMetric)) {
                            const valuesByHorizonId = measurementResult.values[metricOrAggregationMetric];
                            if (!isNil(valuesByHorizonId)) {
                                Object.entries(valuesByHorizonId).forEach(([horizonId, horizonValues]) => {
                                    const castedHorizonValues = horizonValues;
                                    const [firstValue] = castedHorizonValues;
                                    const isAggregateByDevice = !isNil(firstValue.serial);
                                    if (isAggregateByDevice) {
                                        const valuesBySerial = groupBy(castedHorizonValues, (horizonValue) => horizonValue.serial);
                                        Object.entries(valuesBySerial).forEach(([serial, deviceValues]) => {
                                            const deviceDataPoint = deviceValues.map((horizonValue) => ({
                                                date: moment.unix(horizonValue.timestamp).toDate(),
                                                value: horizonValue.value,
                                                source: horizonValue.source
                                            }));
                                            metricDataByDepthOrSerial.set(`${SERIAL_PREFIX}${serial}`, {
                                                data: deviceDataPoint,
                                                domain: extractDomain(deviceDataPoint)
                                            });
                                            metricData = metricData.concat(deviceDataPoint);
                                        });
                                    }
                                    else {
                                        const horizon = measurementResult.horizons?.[metricOrAggregationMetric]?.[parseInt(horizonId, 10)];
                                        const horizonData = castedHorizonValues.map((horizonValue) => ({
                                            date: moment.unix(horizonValue.timestamp).toDate(),
                                            value: horizonValue.value,
                                            source: horizonValue.source
                                        }));
                                        metricDataByDepthOrSerial.set(horizon?.value ? `${horizon.value}` : horizonId, {
                                            data: horizonData,
                                            domain: extractDomain(horizonData)
                                        });
                                        metricData = metricData.concat(horizonData);
                                    }
                                });
                            }
                        }
                        // This test should work since cumulative sums are fetched one at a time
                        else if (!isNil(cumulativeSum) && !isNil(cumulativeMetric)) {
                            const now = new Date();
                            metricData = Object.entries(cumulativeSum).map(([timestamp, cumulativeValue]) => {
                                const date = new Date(parseInt(timestamp, 10) * 1000);
                                return {
                                    date,
                                    value: cumulativeValue,
                                    source: date.getTime() > now.getTime()
                                        ? DataSources.forecast
                                        : DataSources.measurement
                                };
                            });
                        }
                        else if (metricOrAggregationMetric === 'VPD' && !isNil(vpdResult)) {
                            metricData = extractHistoryValues(vpdResult, metricOrAggregationMetric, aggregationType).map((v) => ({
                                date: moment.unix(v.timestamp).toDate(),
                                value: v.value,
                                source: v.source
                            }));
                        }
                        else {
                            metricData = extractHistoryValues(measurementResult, metricOrAggregationMetric, aggregationType).map((v) => ({
                                date: moment.unix(v.timestamp).toDate(),
                                value: originalMetric === 'LW_V'
                                    ? getConverters('LW_V')(v.value, 'lws_intensity_level')
                                    : v.value,
                                source: v.source
                            }));
                        }
                        // COMPUTE METRIC DOMAIN
                        const nonNullMetricDataValues = metricData.reduce((acc, dataPoint) => {
                            if (dataPoint.value !== null) {
                                acc.push(dataPoint.value);
                            }
                            return acc;
                        }, []);
                        let domain = d3.extent(nonNullMetricDataValues);
                        if (domain[0] === undefined) {
                            domain = [0, 0];
                        }
                        domain = getYDomainPerMetric({
                            metric: originalMetric,
                            timeStep,
                            min: !isNil(overriddenDomainByMetric) && originalMetric in overriddenDomainByMetric
                                ? overriddenDomainByMetric[originalMetric]?.[0]
                                : domain[0],
                            max: !isNil(overriddenDomainByMetric) && originalMetric in overriddenDomainByMetric
                                ? overriddenDomainByMetric[originalMetric]?.[1]
                                : domain[1]
                        });
                        data = data.concat(metricData);
                        dataByMetric.set(metricOrAggregationMetric, metricData);
                        if (isMetricWithHorizon(metricOrAggregationMetric)) {
                            dataByMetricBySerialOrHorizon.set(metricOrAggregationMetric, metricDataByDepthOrSerial);
                        }
                        // COMPUTE dataByDateByMetric
                        if (isMetricWithHorizon(metricOrAggregationMetric)) {
                            for (const [depthOrSerial, horizonData] of metricDataByDepthOrSerial) {
                                horizonData.data.forEach((dataPoint) => {
                                    const pointDate = dataPoint.date.getTime();
                                    // first get by time
                                    let metricDataAtDate = dataPointByDateByMetricWithHorizonByHorizon.get(pointDate);
                                    if (metricDataAtDate === undefined) {
                                        metricDataAtDate = new Map();
                                        dataPointByDateByMetricWithHorizonByHorizon.set(pointDate, metricDataAtDate);
                                    }
                                    // then get by metric
                                    let horizonDataAtDate = metricDataAtDate.get(metricOrAggregationMetric);
                                    if (horizonDataAtDate === undefined) {
                                        horizonDataAtDate = new Map();
                                        metricDataAtDate.set(metricOrAggregationMetric, horizonDataAtDate);
                                    }
                                    // then set for this depth date and metric
                                    // deep clone here to create a new reference and keep the value identical to the original and avoid effect from mutation down later (normalization of value)
                                    horizonDataAtDate.set(depthOrSerial, { ...dataPoint });
                                });
                            }
                        }
                        else {
                            metricData.forEach((dataPoint) => {
                                const pointDate = dataPoint.date.getTime();
                                let metricDataAtPointDate = dataByDateByMetric.get(pointDate);
                                if (metricDataAtPointDate === undefined) {
                                    metricDataAtPointDate = new Map();
                                    dataByDateByMetric.set(pointDate, metricDataAtPointDate);
                                }
                                //deep clone here to create a new reference and keep the value identical to the original and avoid effect from mutation down later (normalization of value)
                                metricDataAtPointDate.set(metricOrAggregationMetric, { ...dataPoint });
                            });
                        }
                        // COMPUTE scale by unit
                        const metricUnit = getUnits(originalMetric)[0];
                        let metricNormalizer = normalizerByUnit.get(metricUnit);
                        // if this metric is the first of its unit we create a linear scale
                        if (metricNormalizer === undefined) {
                            const scaleLinear = d3.scaleLinear(domain, [0, 1]);
                            metricNormalizer = {
                                scale: scaleLinear,
                                domain
                            };
                        }
                        // else we concatenate domain of previous metric sharing the same unit with current metric
                        // and create a scale linear with concatenated domains
                        else {
                            let concatenatedDomain = d3.extent(metricNormalizer.domain.concat(domain));
                            if (concatenatedDomain[0] === undefined) {
                                concatenatedDomain = [0, 0];
                            }
                            const scaleLinear = d3.scaleLinear(getYDomainPerMetric({
                                metric: originalMetric,
                                timeStep,
                                min: concatenatedDomain[0],
                                max: concatenatedDomain[1]
                            }), [0, 1]);
                            metricNormalizer = {
                                scale: scaleLinear,
                                domain: concatenatedDomain
                            };
                        }
                        normalizerByUnit.set(metricUnit, metricNormalizer);
                    });
                    // COMPUTE AGGREGATES
                    Array.from(dataByMetric.keys()).forEach((mk) => {
                        if (mk === 'RRAccumulation') {
                            const rainyDays = getValuesCountOverThreshold({
                                threshold: RAIN_THRESHOLD,
                                dataPoints: dataByMetric.get(mk) ?? [],
                                isCumulativeSum: true
                            });
                            aggregationsByMetric.set(mk, {
                                min: null,
                                max: null,
                                avg: null,
                                sum: null,
                                count: rainyDays
                            });
                        }
                        else if (!isMetricWithHorizon(mk) &&
                            !isMetricAggregates(mk) &&
                            !isMeasurementReportType(mk)) {
                            let dataForAggregations = [];
                            if (mk === 'T') {
                                dataForAggregations = [
                                    ...(dataByMetric.get('TN') ?? []),
                                    ...(dataByMetric.get('TX') ?? []),
                                    ...(dataByMetric.get('T') ?? [])
                                ];
                            }
                            else if (mk === 'U') {
                                dataForAggregations = [
                                    ...(dataByMetric.get('UN') ?? []),
                                    ...(dataByMetric.get('UX') ?? []),
                                    ...(dataByMetric.get('U') ?? [])
                                ];
                            }
                            else if (mk === 'FF') {
                                dataForAggregations = [
                                    ...(dataByMetric.get('FXY') ?? []),
                                    ...(dataByMetric.get('FF') ?? [])
                                ];
                            }
                            else {
                                dataForAggregations = dataByMetric.get(mk) ?? [];
                            }
                            const aggregationType = getPeriodAggregationPerMetric(mk);
                            const metricAggregations = extractAggregations(dataForAggregations, aggregationType);
                            aggregationsByMetric.set(mk, metricAggregations);
                        }
                    });
                    return {
                        aggregationsByMetric,
                        data,
                        dataByDateByMetric,
                        dataByMetric,
                        dataByMetricBySerialOrHorizon,
                        dataPointByDateByMetricWithHorizonByHorizon,
                        normalizerByUnit,
                        thresholds
                    };
                }
            };
            return result;
        }
    };
});
export default charts;
